/**
 * Anbaugeräte Carousel - Interactive Ring Configurator
 * SeKa Baumaschinen
 * 
 * Features:
 * - Circular carousel on desktop
 * - Horizontal slider on mobile
 * - Drag/swipe support
 * - Mouse wheel rotation
 * - Parallax effect on excavator
 * - Modal popups
 */

(function() {
  'use strict';

  class AnbaugeraeteCarousel {
    constructor(container) {
      this.container = container;
      this.ring = container.querySelector('.carousel-ring');
      this.items = Array.from(container.querySelectorAll('.carousel-item'));
      this.excavator = container.querySelector('.excavator-wrapper');
      this.modal = container.querySelector('.anbaugeraete-modal-overlay');
      this.prevBtn = container.querySelector('.carousel-nav--prev');
      this.nextBtn = container.querySelector('.carousel-nav--next');
      
      // State
      this.rotation = 0;
      this.targetRotation = 0;
      this.velocity = 0;
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this.lastX = 0;
      this.isMobile = window.innerWidth < 1024;
      this.activeIndex = 0;
      this.itemCount = this.items.length;
      this.anglePerItem = 360 / this.itemCount;
      
      // Parallax state
      this.mouseX = 0;
      this.mouseY = 0;
      this.parallaxEnabled = this.excavator?.dataset.parallax === 'true';
      
      // RAF
      this.animationFrame = null;
      
      this.init();
    }
    
    init() {
      this.positionItems();
      this.bindEvents();
      this.startAnimation();
      this.updateActiveItem();
    }
    
    positionItems() {
      if (this.isMobile) return;
      
      const ringRect = this.ring.getBoundingClientRect();
      const radius = Math.min(ringRect.width, ringRect.height) / 2 - 60;
      
      this.items.forEach((item, index) => {
        const angle = (index * this.anglePerItem - 90) * (Math.PI / 180);
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        
        item.style.transform = `translate(${x}px, ${y}px)`;
        item.dataset.baseAngle = index * this.anglePerItem;
      });
    }
    
    bindEvents() {
      // Resize
      window.addEventListener('resize', this.handleResize.bind(this));
      
      // Mouse/Touch events for ring
      if (!this.isMobile) {
        this.ring.addEventListener('mousedown', this.handleDragStart.bind(this));
        this.ring.addEventListener('touchstart', this.handleDragStart.bind(this), { passive: false });
        window.addEventListener('mousemove', this.handleDragMove.bind(this));
        window.addEventListener('touchmove', this.handleDragMove.bind(this), { passive: false });
        window.addEventListener('mouseup', this.handleDragEnd.bind(this));
        window.addEventListener('touchend', this.handleDragEnd.bind(this));
        
        // Mouse wheel
        this.ring.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
        
        // Parallax
        if (this.parallaxEnabled) {
          this.container.addEventListener('mousemove', this.handleParallax.bind(this));
          this.container.addEventListener('mouseleave', this.resetParallax.bind(this));
        }
      }
      
      // Navigation buttons
      this.prevBtn?.addEventListener('click', () => this.rotate(-1));
      this.nextBtn?.addEventListener('click', () => this.rotate(1));
      
      // Item clicks
      this.items.forEach((item, index) => {
        item.addEventListener('click', (e) => this.handleItemClick(e, index));
      });
      
      // Modal
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.closest('.anbaugeraete-modal-close')) {
          this.closeModal();
        }
      });
      
      // Keyboard
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeModal();
        if (e.key === 'ArrowLeft') this.rotate(-1);
        if (e.key === 'ArrowRight') this.rotate(1);
      });
    }
    
    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 1024;
      
      if (wasMobile !== this.isMobile) {
        this.positionItems();
      }
    }
    
    handleDragStart(e) {
      if (this.isMobile) return;
      
      this.isDragging = true;
      this.velocity = 0;
      
      const point = e.touches ? e.touches[0] : e;
      this.startX = point.clientX;
      this.startY = point.clientY;
      this.lastX = point.clientX;
      
      this.ring.style.cursor = 'grabbing';
    }
    
    handleDragMove(e) {
      if (!this.isDragging || this.isMobile) return;
      
      const point = e.touches ? e.touches[0] : e;
      const deltaX = point.clientX - this.lastX;
      
      // Calculate rotation based on drag
      this.velocity = deltaX * 0.5;
      this.targetRotation += this.velocity;
      
      this.lastX = point.clientX;
      
      if (e.cancelable) e.preventDefault();
    }
    
    handleDragEnd() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      this.ring.style.cursor = 'grab';
      
      // Snap to nearest item
      this.snapToNearest();
    }
    
    handleWheel(e) {
      if (this.isMobile) return;
      
      e.preventDefault();
      
      const delta = e.deltaY > 0 ? 1 : -1;
      this.targetRotation += delta * this.anglePerItem * 0.3;
      
      // Debounce snap
      clearTimeout(this.wheelTimeout);
      this.wheelTimeout = setTimeout(() => this.snapToNearest(), 150);
    }
    
    handleParallax(e) {
      const rect = this.container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      this.mouseX = (e.clientX - centerX) / rect.width;
      this.mouseY = (e.clientY - centerY) / rect.height;
    }
    
    resetParallax() {
      this.mouseX = 0;
      this.mouseY = 0;
    }
    
    rotate(direction) {
      this.targetRotation += direction * this.anglePerItem;
      this.snapToNearest();
    }
    
    snapToNearest() {
      const snappedRotation = Math.round(this.targetRotation / this.anglePerItem) * this.anglePerItem;
      this.targetRotation = snappedRotation;
    }
    
    handleItemClick(e, index) {
      const item = this.items[index];
      const behavior = item.dataset.behavior;
      const link = item.dataset.link;
      
      if (behavior === 'link' && link) {
        window.location.href = link;
        return;
      }
      
      // Modal behavior
      this.openModal(index);
    }
    
    openModal(index) {
      if (!this.modal) return;
      
      const item = this.items[index];
      const name = item.dataset.name;
      const description = item.dataset.description;
      const link = item.dataset.link;
      const iconSrc = item.querySelector('.carousel-item-icon')?.src;
      
      // Badge data
      const badge1 = item.dataset.badge1;
      const badge1Icon = item.dataset.badge1Icon;
      const badge2 = item.dataset.badge2;
      const badge2Icon = item.dataset.badge2Icon;
      const badge3 = item.dataset.badge3;
      const badge3Icon = item.dataset.badge3Icon;
      
      // Badge icons SVG map
      const badgeIcons = {
        check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>',
        shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>',
        star: '<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>',
        award: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>',
        certificate: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"></rect><path d="M8 10h8M8 14h4"></path></svg>',
        rotation: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path></svg>',
        germany: '<svg viewBox="0 0 24 24"><rect y="4" width="24" height="5.33" fill="#000"/><rect y="9.33" width="24" height="5.33" fill="#DD0000"/><rect y="14.67" width="24" height="5.33" fill="#FFCC00"/></svg>',
        hardox: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>',
        weight: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M5.6 18.4L18.4 5.6"></path></svg>',
        cog: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>'
      };
      
      // Update modal content
      const titleEl = this.modal.querySelector('.anbaugeraete-modal-title');
      const descEl = this.modal.querySelector('.anbaugeraete-modal-description');
      const btnEl = this.modal.querySelector('.anbaugeraete-modal-button');
      const iconEl = this.modal.querySelector('.anbaugeraete-modal-icon');
      const badgesEl = this.modal.querySelector('.anbaugeraete-modal-badges');
      
      if (titleEl) titleEl.textContent = name;
      if (descEl) descEl.textContent = description || '';
      if (btnEl) btnEl.href = link || '#';
      
      if (iconEl) {
        if (iconSrc) {
          iconEl.innerHTML = `<img src="${iconSrc}" alt="${name}">`;
        } else {
          iconEl.innerHTML = '<span class="anbaugeraete-modal-icon-fallback">🔧</span>';
        }
      }
      
      // Render badges
      if (badgesEl) {
        const badges = [
          { text: badge1, icon: badge1Icon },
          { text: badge2, icon: badge2Icon },
          { text: badge3, icon: badge3Icon }
        ].filter(b => b.text && b.text.trim());
        
        badgesEl.innerHTML = badges.map(b => `
          <span class="anbaugeraete-modal-badge">
            ${badgeIcons[b.icon] || badgeIcons.check}
            ${b.text}
          </span>
        `).join('');
      }
      
      this.modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
    
    closeModal() {
      if (!this.modal) return;
      
      this.modal.classList.remove('active');
      document.body.style.overflow = '';
    }
    
    updateActiveItem() {
      // Calculate which item is at the top (active position)
      const normalizedRotation = ((this.rotation % 360) + 360) % 360;
      this.activeIndex = Math.round(normalizedRotation / this.anglePerItem) % this.itemCount;
      
      this.items.forEach((item, index) => {
        const isActive = index === this.activeIndex;
        item.classList.toggle('active', isActive);
        item.classList.toggle('inactive', !isActive);
      });
    }
    
    startAnimation() {
      const animate = () => {
        // Smooth rotation interpolation
        const diff = this.targetRotation - this.rotation;
        this.rotation += diff * 0.08;
        
        // Apply inertia when dragging
        if (!this.isDragging && Math.abs(this.velocity) > 0.1) {
          this.velocity *= 0.95;
          this.targetRotation += this.velocity;
        }
        
        // Update positions on desktop
        if (!this.isMobile) {
          this.updateRingPosition();
        }
        
        // Update parallax
        if (this.parallaxEnabled && this.excavator) {
          const maxShift = 6;
          const currentX = parseFloat(this.excavator.dataset.currentX || 0);
          const currentY = parseFloat(this.excavator.dataset.currentY || 0);
          const targetX = this.mouseX * maxShift;
          const targetY = this.mouseY * maxShift;
          
          const newX = currentX + (targetX - currentX) * 0.1;
          const newY = currentY + (targetY - currentY) * 0.1;
          
          this.excavator.style.transform = `translate(${newX}px, ${newY}px)`;
          this.excavator.dataset.currentX = newX;
          this.excavator.dataset.currentY = newY;
        }
        
        this.updateActiveItem();
        
        this.animationFrame = requestAnimationFrame(animate);
      };
      
      animate();
    }
    
    updateRingPosition() {
      const ringRect = this.ring.getBoundingClientRect();
      const radius = Math.min(ringRect.width, ringRect.height) / 2 - 60;
      
      this.items.forEach((item, index) => {
        const baseAngle = parseFloat(item.dataset.baseAngle);
        const currentAngle = (baseAngle - this.rotation - 90) * (Math.PI / 180);
        const x = Math.cos(currentAngle) * radius;
        const y = Math.sin(currentAngle) * radius;
        
        item.style.transform = `translate(${x}px, ${y}px)`;
      });
    }
    
    destroy() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
    }
  }
  
  // Initialize on DOM ready
  function init() {
    const containers = document.querySelectorAll('.anbaugeraete-section');
    containers.forEach(container => new AnbaugeraeteCarousel(container));
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
